import React from 'react';
import {
  Button,
  Center,
  Grid,
  Stack,
  Title,
  Image,
  Card,
  List,
  ThemeIcon,
  Text,
  Box,
  Group,
  Badge,
  Flex,
  Styles,
  BadgeStylesParams,
} from '@mantine/core';
import { IconExternalLink, IconLink, IconPencil, IconPlaystationCircle, IconRadar, IconTag } from '@tabler/icons';
import { NavLink, useLoaderData } from 'react-router-dom';
import DataTable from '../../Components/DataTable';
import useUserData from '../../Hooks/use-user-data';
import { createTechnologyTableData, getLogoUrl } from './shared';
import { TechnologyLoaderResult } from '../../Loaders/TechnologyLoader';
import { DEFAULT_BLIP_COLOR } from './Form';

export function hasDescription(description: string | undefined) {
  return description !== undefined && description !== '<p></p>';
}

const Technology = () => {
  const { technology, persons } = useLoaderData() as TechnologyLoaderResult;
  const { isAdministrator } = useUserData();
  const hasBackgroundColor = technology.blipColor && technology.blipColor !== DEFAULT_BLIP_COLOR;
  return (
    <Center>
      <Card maw={960} w={'100%'} withBorder shadow="sm" p="xl" radius="md">
        <Card.Section
          withBorder={!!hasBackgroundColor}
          inheritPadding
          py={'xl'}
          // setting an alpha value of 20 in HEX = 5% in rgb world
          {...(hasBackgroundColor && { sx: { backgroundColor: `${technology.blipColor}20` } })}
        >
          <Stack spacing={'md'}>
            {technology.logoUrl && (
              <Image width={'auto'} height={75} maw={'100%'} fit="contain" src={getLogoUrl(technology.logoUrl)} />
            )}
            <Grid>
              <Grid.Col md={6} display={'flex'}>
                <Flex direction={'column'} gap={'xs'} wrap={'wrap'} align={'flex-start'} w={'100%'}>
                  <Title order={1}>{technology.name}</Title>
                  {technology.url && (
                    <Button
                      component="a"
                      href={technology.url}
                      target="_blank"
                      variant="outline"
                      rightIcon={<IconExternalLink size={14} />}
                    >
                      Go to homepage
                    </Button>
                  )}
                </Flex>
              </Grid.Col>
              <Grid.Col md={6}>
                <List spacing="xs" size="sm" center>
                  <List.Item
                    icon={
                      <ThemeIcon size={24} radius="xl">
                        <IconRadar size={16} />
                      </ThemeIcon>
                    }
                  >
                    <Text fw={500} fz={'lg'}>
                      {technology.section?.name}
                    </Text>
                  </List.Item>
                  <List.Item
                    icon={
                      <ThemeIcon size={24} radius="xl">
                        <IconPlaystationCircle size={16} />
                      </ThemeIcon>
                    }
                  >
                    <Text fw={500} fz={'lg'}>
                      {technology.ring?.name}
                    </Text>
                  </List.Item>
                  {technology.tags && technology.tags.length > 0 && (
                    <List.Item
                      icon={
                        <ThemeIcon size={24} radius="xl">
                          <IconTag size={16} />
                        </ThemeIcon>
                      }
                    >
                      <Flex gap={'xs'} align={'center'} wrap={'wrap'}>
                        {technology.tags.map((tag) => (
                          <Badge variant="outline" key={tag.id} color={'cyan'} styles={BadgeDecorator}>
                            {tag.name}
                          </Badge>
                        ))}
                      </Flex>
                    </List.Item>
                  )}
                </List>
              </Grid.Col>
            </Grid>
          </Stack>
        </Card.Section>
        <Card.Section inheritPadding pb={'xl'}>
          <Stack spacing={'md'}>
            {technology.clients && technology.clients.length > 0 && (
              <Box>
                <Title order={3} mb={'xs'} mt={'sm'}>
                  Related clients
                </Title>
                <Group spacing={'xs'}>
                  {technology.clients.map((client) => (
                    <NavLink to={`/client/${encodeURIComponent(client.name)}`} key={client.id}>
                      <Button variant="outline" color={'orange'} size="xs" rightIcon={<IconLink size={16} />}>
                        {client.name}
                      </Button>
                    </NavLink>
                  ))}
                </Group>
              </Box>
            )}
            <DataTable data={createTechnologyTableData(technology, persons)} />
            {hasDescription(technology.description) && (
              <Box dangerouslySetInnerHTML={{ __html: technology.description as string }} />
            )}
            {isAdministrator && (
              <Flex justify={'flex-end'} align={'center'}>
                <Button component={NavLink} miw={160} to={'edit'} variant="filled" rightIcon={<IconPencil size={16} />}>
                  Edit
                </Button>
              </Flex>
            )}
          </Stack>
        </Card.Section>
      </Card>
    </Center>
  );
};

export default Technology;

export const BadgeDecorator: Styles<'leftSection' | 'rightSection' | 'inner' | 'root', BadgeStylesParams> = {
  inner: { textTransform: 'initial' },
};
